// The @ picker, and the state behind it — in one place, for the composer and
// the close sheet. Lifted from the Andon app's `MentionPicker`.
//
// THE PICKED LOGINS ARE THE TRUTH, NOT THE TEXT.
//
// `lib/mentions.ts` has the rules; this owns the state and the list. The body
// carries `@Ben Curtis` because that is what reads on a thread, and the SERVER
// is sent the login list from the picker: names are not unique, people get
// renamed, and a name matched server-side out of free text would silently notify
// nobody on a typo — or notify the wrong Ben. `stillMentioned` is what keeps the
// two honest when somebody backspaces over a name they inserted.
//
// THE ROSTER IS NOT THE RESPONDER LIST.
//
// `/andon/roster` answers "who can I tell"; `/andon/responders` answers "who can
// I hand this to". They were one endpoint, which is exactly why the picker could
// offer the routing table and not the maintenance fitter twenty metres away. The
// scope is Odoo's — `andon.policy.mention_roster` — so it cannot be widened from
// here.

import { useCallback, useMemo, useRef, useState } from 'react';
import { andonAvatarUrl } from '@/api/andon/client';
import type { RosterPerson } from '@/api/andon/types';
import { useT } from '@/i18n/useT';
import { applyMention, matches, mentionAt, stillMentioned } from '@/lib/mentions';
import { useMentionRoster } from '@/queries/andon';
import { OwnerAvatar } from './primitives';

export type { RosterPerson };

export interface Mentions {
  /** The people offered for what is being typed. Empty closes the picker. */
  suggestions: RosterPerson[];
  /** Insert the person at the caret. */
  pick: (person: RosterPerson) => void;
  /** Where the caret should go after React commits the longer string. */
  pendingCaret: React.MutableRefObject<number | null>;
  /** The logins to send: picked, AND still written in the draft. */
  logins: (text: string) => string[];
  /** Everybody picked this message. */
  picked: RosterPerson[];
  /** After a send, or a discarded draft. */
  reset: () => void;
  /** Put a whole list back, when a failed send restores its draft. */
  restore: (people: RosterPerson[]) => void;
}

/**
 * The picker's state, for a caller that owns the text.
 *
 * A hook rather than a component wrapping the input, because the callers have
 * different fields — a composer bar, a close sheet's textarea — and wrapping
 * the input would mean one component pretending to be both.
 */
export function useMentions(text: string, caret: number, setText: (next: string) => void): Mentions {
  const [picked, setPicked] = useState<RosterPerson[]>([]);
  const pendingCaret = useRef<number | null>(null);

  // Fetched once and cached: the picker is not worth a round trip per keystroke
  // on a factory network, and the roster changes on the timescale of somebody
  // being hired.
  const { data: people = [] } = useMentionRoster();

  const query = useMemo(() => mentionAt(text, caret), [text, caret]);
  const suggestions = useMemo(() => (query ? matches(people, query.term) : []), [query, people]);

  const pick = useCallback(
    (person: RosterPerson) => {
      if (!query) return;
      const out = applyMention(text, query, person);
      setText(out.text);
      setPicked((prev) => (prev.some((p) => p.login === person.login) ? prev : [...prev, person]));
      // The caret goes back where the insertion left it, not to the end: people
      // mention somebody in the middle of a sentence they are still writing.
      pendingCaret.current = out.caret;
    },
    [query, setText, text],
  );

  return {
    suggestions,
    pick,
    pendingCaret,
    // The TEXT has the final say on the way out. A mention that is no longer
    // written is a person subscribed to an andon with no visible reason why.
    logins: useCallback((body: string) => stillMentioned(body, picked), [picked]),
    picked,
    reset: useCallback(() => setPicked([]), []),
    restore: useCallback((next: RosterPerson[]) => setPicked(next), []),
  };
}

/**
 * The list itself.
 *
 * `above` is which side of the field it sits on, and it is not decoration. In
 * the composer the box is at the bottom of the screen with a keyboard under it,
 * so the list has to go up. On the close sheet the field is mid-sheet and the
 * list belongs DIRECTLY UNDER it.
 */
export function MentionList({
  suggestions,
  onPick,
  above = true,
  label,
}: {
  suggestions: RosterPerson[];
  onPick: (person: RosterPerson) => void;
  above?: boolean;
  /** The heading over the list, which says what picking somebody does here. */
  label: string;
}) {
  const t = useT();
  if (suggestions.length === 0) return null;
  return (
    <div
      role="listbox"
      aria-label={t.mentionSomeoneT}
      style={{
        marginBottom: above ? 10 : 0,
        marginTop: above ? 0 : 8,
        borderRadius: 14,
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        overflow: 'hidden',
        boxShadow: 'var(--elev)',
      }}
    >
      <div style={{ padding: '8px 14px 6px', font: "700 9px 'Space Grotesk',sans-serif", letterSpacing: '.09em', color: 'var(--sub)', textTransform: 'uppercase' }}>{label}</div>
      {suggestions.map((person, i) => (
        <button
          key={person.login}
          type="button"
          role="option"
          aria-selected={i === 0}
          className="press-row"
          // `onMouseDown` rather than `onClick`: a click fires after the input
          // has already lost focus, which on a phone closes the keyboard and
          // scrolls the thread. Preventing the default keeps the caret put.
          onMouseDown={(e) => {
            e.preventDefault();
            onPick(person);
          }}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            textAlign: 'left',
            background: i === 0 ? 'var(--surface2)' : 'transparent',
          }}
        >
          <OwnerAvatar name={person.name} size={26} photoUrl={andonAvatarUrl(person)} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', font: '600 13px Manrope,sans-serif', color: 'var(--ink)' }}>{person.name}</span>
            {/* The team, and only a POSITIVE shift badge. Two Deans in the list
                is the ordinary case — word-prefix matching puts both there — and
                the department is what tells them apart. */}
            {(person.department || person.onShift) && (
              <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 1 }}>
                {[person.department, person.onShift ? t.onShiftT : null].filter(Boolean).join(' · ')}
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}
